import { useCallback, useMemo } from 'react'

import { Features } from 'generated/featureDefinitions'
import {
  CreateCustomFieldFieldDocument,
  DeleteCustomFieldFieldDocument,
  GetLegalEntityContractSettingsDocument,
  UpdateCustomFieldFieldDocument,
  UpdateCustomFieldResourceDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CustomFieldsField } from 'modules/legal_entities/pages/settings/ContractsLegalEntityPage/ContractFields/types'

type Props = {
  companyId: number
  legalEntityId: number
  field?: CustomFieldsField
  setModalType: (type: 'none' | 'edit' | 'new') => void
}

const useCustomFieldMutations = ({ companyId, legalEntityId, field, setModalType }: Props) => {
  const fetchQueries = [
    {
      query: GetLegalEntityContractSettingsDocument,
      variables: {
        companyId: companyId,
        legalEntityId: legalEntityId,
        featureAvailabilityIds: [Features.compensations_management],
      },
    },
  ]

  const showSuccess = useCallback(() => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
    })
  }, [])

  const showError = useCallback((errors: Record<string, string>) => {
    app.setFlash({
      type: 'wrong',
      message: errors?.base || errors?.messages || i18n.t('errors.generic_error'),
    })
  }, [])

  const [createCustomfieldsField, { loading: loadingCreateCustomField }] = useMutation(
    CreateCustomFieldFieldDocument,
    {
      extractorFn: (data) => data.customFieldsMutations.createCustomfieldsField,
      refetchQueries: fetchQueries,
    }
  )

  const [deleteCustomfieldsField, { loading: loadingDeleteCustomField }] = useMutation(
    DeleteCustomFieldFieldDocument,
    {
      extractorFn: (data) => data.customFieldsMutations.deleteCustomfieldsField,
      refetchQueries: fetchQueries,
    }
  )

  const [updateCustomfieldsField, { loading: loadingUpdateCustomField }] = useMutation(
    UpdateCustomFieldFieldDocument,
    {
      extractorFn: (data) => data.customFieldsMutations.updateCustomfieldsField,
      refetchQueries: fetchQueries,
    }
  )

  const [updateCustomfieldsResourceField, { loading: loadingUpdateCustomFieldResource }] =
    useMutation(UpdateCustomFieldResourceDocument, {
      extractorFn: (data) => data.customFieldsMutations.updateCustomfieldsResourceField,
      refetchQueries: fetchQueries,
    })

  const handleDelete = useCallback(async () => {
    if (!field?.id) return null

    const result = await deleteCustomfieldsField({
      variables: {
        fieldId: field.id,
      },
    })

    const handled = handleResult(result, (errors) => {
      showError(errors)
    })

    if (handled.success) {
      setModalType('none')
      showSuccess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, deleteCustomfieldsField])

  const handleCreateCustomField = useCallback(async () => {
    if (!field || !field.fieldType) return null

    const result = await createCustomfieldsField({
      variables: {
        companyId: companyId,
        editable: 'owned',
        fieldType: field.fieldType,
        label: field.labelText,
        options: field.options.map((option) => option.label || ''),
        required: field.required ?? false,
        visible: 'owned',
        usageGroupSlug: 'contract-versions',
        fieldableId: legalEntityId,
        fieldableType: 'LegalEntity',
      },
    })

    const handled = handleResult(result, (errors) => {
      showError(errors)
    })

    if (handled.success) {
      setModalType('none')
      showSuccess()
    }

    return handled
  }, [
    field,
    createCustomfieldsField,
    companyId,
    legalEntityId,
    showError,
    setModalType,
    showSuccess,
  ])

  const handleUpdateCustomField = useCallback(
    async (field?: CustomFieldsField) => {
      if (!field || !field.id) return null

      const formatChoices = field.options.map((option) => {
        return {
          id: option.id,
          label: option.label,
          isActive: option.isActive,
        }
      })

      const result = await updateCustomfieldsField({
        variables: {
          choiceOptions: formatChoices,
          fieldId: field.id,
          label: field.labelText,
          required: field.required ?? false,
          companyId: companyId,
        },
      })

      const handled = handleResult(result, (errors) => {
        showError(errors)
      })

      if (handled.success) {
        setModalType('none')
        showSuccess()
      }

      return handled
    },
    [updateCustomfieldsField, companyId, showError, setModalType, showSuccess]
  )

  const handleChangePosition = useCallback(
    async (resourceId: number, position: number) => {
      const result = await updateCustomfieldsResourceField({
        variables: {
          resourceId: resourceId,
          position: position,
        },
      })

      handleResult(result, (errors) => {
        showError(errors)
      })
    },
    [updateCustomfieldsResourceField, showError]
  )

  const mutations = useMemo(
    () => ({
      handleCreateCustomField,
      handleDelete,
      handleUpdateCustomField,
      handleChangePosition,
      loadingCreateCustomField,
      loadingDeleteCustomField,
      loadingUpdateCustomField,
      loadingUpdateCustomFieldResource,
    }),
    [
      handleCreateCustomField,
      handleDelete,
      handleUpdateCustomField,
      handleChangePosition,
      loadingCreateCustomField,
      loadingDeleteCustomField,
      loadingUpdateCustomField,
      loadingUpdateCustomFieldResource,
    ]
  )

  return mutations
}

export default useCustomFieldMutations
